import { HealthPassCardFront } from "@/components/card/HealthPassCardFront";
import { planQuery } from "@/utils/planIntent";
import { PublicNav } from "@/components/PublicNav";
import { SEO } from "@/components/SEO";
import { JsonLd, ORGANIZATION, SITE_URL } from "@/components/JsonLd";
import type { CardDTO, CardQR } from "@/services/api/cardService";
import { useI18nStore } from "@/stores/i18nStore";
import { useStandaloneDisplay } from "@/hooks/useStandaloneDisplay";
import { useLocaleFormat } from "@/hooks/useLocaleFormat";
import type { LucideIcon } from "lucide-react";
import {
  Activity,
  ArrowRight,
  BarChart3,
  Bell,
  Brain,
  Building2,
  Calendar,
  CheckCircle2,
  Clock,
  Globe,
  Heart,
  Lock,
  Plane,
  QrCode,
  Shield,
  Smartphone,
  Stethoscope,
  Users,
  Wallet,
} from "lucide-react";
import { useEffect } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { AppInstallSection } from "../components/landing/AppInstallSection";
import { QuickInstallSection } from "../components/landing/QuickInstallSection";

const landingIconFrameClassName =
  "inline-flex h-14 w-14 items-center justify-center rounded-2xl border border-slate-200 bg-slate-50 text-slate-700 shadow-sm";
const landingStoryTitleClassName =
  "text-xl sm:text-2xl font-bold leading-tight tracking-tight text-gray-900 mb-2";

const landingDemoCardData: CardDTO = {
  cardNumber: "HP-3B4F-D747-793B",
  issuedAt: "2026-04-01T00:00:00.000Z",
  validUntil: "2027-04-30T00:00:00.000Z",
  cardVersion: 1,
  subscriptionTier: "PLUS",
  holder: {
    firstName: "Terry",
    lastName: "Pazzo",
    dateOfBirth: "1985-06-13",
    gender: "male",
    nationality: "Canadian",
    avatarUrl: null,
  },
  emergencySummary: null,
};

const landingDemoQrData: CardQR = {
  qrDataUrl:
    "data:image/svg+xml;utf8," +
    encodeURIComponent(`
      <svg xmlns="http://www.w3.org/2000/svg" width="120" height="120" viewBox="0 0 120 120">
        <rect width="120" height="120" fill="white"/>
        <rect x="8" y="8" width="28" height="28" fill="black"/>
        <rect x="13" y="13" width="18" height="18" fill="white"/>
        <rect x="17" y="17" width="10" height="10" fill="black"/>
        <rect x="84" y="8" width="28" height="28" fill="black"/>
        <rect x="89" y="13" width="18" height="18" fill="white"/>
        <rect x="93" y="17" width="10" height="10" fill="black"/>
        <rect x="8" y="84" width="28" height="28" fill="black"/>
        <rect x="13" y="89" width="18" height="18" fill="white"/>
        <rect x="17" y="93" width="10" height="10" fill="black"/>
        <g fill="black">
          <rect x="46" y="10" width="6" height="6"/>
          <rect x="58" y="10" width="6" height="6"/>
          <rect x="70" y="10" width="6" height="6"/>
          <rect x="46" y="22" width="6" height="6"/>
          <rect x="58" y="22" width="6" height="6"/>
          <rect x="40" y="40" width="6" height="6"/>
          <rect x="52" y="40" width="6" height="6"/>
          <rect x="64" y="40" width="6" height="6"/>
          <rect x="76" y="40" width="6" height="6"/>
          <rect x="46" y="52" width="6" height="6"/>
          <rect x="58" y="52" width="6" height="6"/>
          <rect x="70" y="52" width="6" height="6"/>
          <rect x="82" y="52" width="6" height="6"/>
          <rect x="40" y="64" width="6" height="6"/>
          <rect x="52" y="64" width="6" height="6"/>
          <rect x="64" y="64" width="6" height="6"/>
          <rect x="76" y="64" width="6" height="6"/>
          <rect x="88" y="64" width="6" height="6"/>
          <rect x="46" y="76" width="6" height="6"/>
          <rect x="58" y="76" width="6" height="6"/>
          <rect x="70" y="76" width="6" height="6"/>
          <rect x="82" y="76" width="6" height="6"/>
          <rect x="100" y="76" width="6" height="6"/>
          <rect x="40" y="88" width="6" height="6"/>
          <rect x="52" y="88" width="6" height="6"/>
          <rect x="64" y="88" width="6" height="6"/>
          <rect x="76" y="88" width="6" height="6"/>
          <rect x="88" y="88" width="6" height="6"/>
          <rect x="100" y="88" width="6" height="6"/>
        </g>
      </svg>
    `),
  offlineFhirQrDataUrl: "",
  accessToken: "landing-demo",
  onlineUrl: "https://travelhealthpass.app",
};

function LandingIcon({
  icon: Icon,
  className = "",
}: {
  icon: LucideIcon;
  className?: string;
}) {
  return (
    <div className={`${landingIconFrameClassName} ${className}`.trim()}>
      <Icon className="h-7 w-7" />
    </div>
  );
}

export function LandingPage() {
  const { t, resetToEnglish } = useI18nStore();
  const fmt = useLocaleFormat();
  const [searchParams] = useSearchParams();
  const targetPlan = searchParams.get("plan") || "traveler"; // Default: All Travelers
  const standalone = useStandaloneDisplay();

  useEffect(() => {
    resetToEnglish();
  }, [resetToEnglish]);

  // Someone who opened the installed app is past being sold to: they tapped an
  // icon on their own home screen. This page is the marketing site, and it
  // shows here only because App routes "/" to it while signed out - so from
  // inside the app it looks as though the app never starts.
  //
  // Only reachable signed out; the signed-in tree serves the dashboard at "/".
  if (standalone) {
    return <Navigate to="/traveler/login" replace />;
  }

  // The amounts are the same in both currencies; which one is shown follows
  // the language, as checkout does (detectCurrency): dollars in English, euros
  // otherwise. The English page is the one assistants read, and it has to
  // quote the price the records page and the blog quote.
  const priceCurrency = fmt.locale.startsWith("en") ? "USD" : "EUR";
  const formatPrice = (amount: number) =>
    new Intl.NumberFormat(fmt.locale, {
      style: "currency",
      currency: priceCurrency,
      maximumFractionDigits: 0,
    }).format(amount);
  const monthDay = (d: Date) =>
    new Intl.DateTimeFormat(fmt.locale, {
      month: "short",
      day: "numeric",
    }).format(d);
  const mockTripDates = `${monthDay(new Date(2027, 2, 15))} – ${monthDay(
    new Date(2027, 2, 28),
  )}`;
  const mockReminderTime = fmt.time(new Date(2027, 2, 15, 9, 0));
  const percentAvoiding = new Intl.NumberFormat(fmt.locale, {
    style: "percent",
  }).format(0.4);

  // Dynamic Hero content by target audience
  const heroContent = {
    traveler: {
      headline: t.miscLandHeroTravelerHeadline,
      subheadline: t.miscLandHeroTravelerSubheadline,
      cta: t.landingStartFree,
      ctaLink: "/traveler/register",
      image: "/images/traveler-hero.jpg",
    },
    nomad: {
      headline: t.miscLandHeroNomadHeadline,
      subheadline: t.miscLandHeroNomadSubheadline,
      cta: t.miscLandCtaCreateFreeAccount,
      ctaLink: "/traveler/register?plan=nomad",
      image: "/images/nomad-hero.jpg",
    },
    corporate: {
      headline: t.miscLandHeroCorporateHeadline,
      subheadline: t.miscLandHeroCorporateSubheadline,
      cta: t.miscLandCtaRequestDemo,
      ctaLink: "/book-a-demo",
      image: "/images/business-hero.jpg",
    },
    family: {
      headline: t.miscLandHeroFamilyHeadline,
      subheadline: t.miscLandHeroFamilySubheadline,
      cta: t.miscLandCtaCreateFreeAccount,
      ctaLink: "/traveler/register?plan=family",
      image: "/images/family-hero.jpg",
    },
    chronic: {
      headline: t.miscLandHeroChronicHeadline,
      subheadline: t.miscLandHeroChronicSubheadline,
      cta: t.miscLandCtaCreateFreeAccount,
      ctaLink: "/traveler/register?plan=chronic",
      image: "/images/chronic-hero.jpg",
    },
  };

  const currentHero =
    heroContent[targetPlan as keyof typeof heroContent] || heroContent.traveler;

  const audiences = [
    t.miscLandAudienceHealthMinded,
    t.miscLandAudienceHealthIssues,
    t.miscLandAudienceOlder,
    t.miscLandAudienceDisabled,
    t.miscLandAudienceActivities,
    t.miscLandAudiencePlaneCruise,
  ];

  const features = [
    {
      icon: Heart,
      title: t.miscLandFeatureEmergencyTitle,
      description: t.miscLandFeatureEmergencyDesc,
      color: "text-red-600",
    },
    {
      icon: Shield,
      title: t.miscLandFeatureGdprTitle,
      description: t.miscLandFeatureGdprDesc,
      color: "text-primary-600",
    },
    {
      icon: Plane,
      title: t.miscLandFeatureTravelTitle,
      description: t.miscLandFeatureTravelDesc,
      color: "text-primary-600",
    },
    {
      icon: Clock,
      title: t.miscLandFeatureMedsTitle,
      description: t.miscLandFeatureMedsDesc,
      color: "text-green-600",
    },
    {
      icon: Globe,
      title: t.miscLandFeatureLanguagesTitle,
      description: t.miscLandFeatureLanguagesDesc,
      color: "text-primary-600",
    },
    {
      icon: Smartphone,
      title: t.miscLandFeatureMobileTitle,
      description: t.miscLandFeatureMobileDesc,
      color: "text-pink-600",
    },
  ];

  const steps = [
    {
      number: "1",
      title: t.miscLandStep1Title,
      description: t.miscLandStep1Desc,
    },
    {
      number: "2",
      title: t.miscLandStep2Title,
      description: t.miscLandStep2Desc,
    },
    {
      number: "3",
      title: t.miscLandStep3Title,
      description: t.miscLandStep3Desc,
    },
  ];

  const chronicCards = [
    {
      icon: Activity,
      title: t.miscLandChronicDiabetesTitle,
      points: [
        t.miscLandChronicDiabetes1,
        t.miscLandChronicDiabetes2,
        t.miscLandChronicDiabetes3,
        t.miscLandChronicDiabetes4,
      ],
    },
    {
      icon: Stethoscope,
      title: t.miscLandChronicIbdTitle,
      points: [
        t.miscLandChronicIbd1,
        t.miscLandChronicIbd2,
        t.miscLandChronicIbd3,
        t.miscLandChronicIbd4,
      ],
    },
    {
      icon: Brain,
      title: t.miscLandChronicNeuroTitle,
      points: [
        t.miscLandChronicNeuro1,
        t.miscLandChronicNeuro2,
        t.miscLandChronicNeuro3,
        t.miscLandChronicHospitalsNearby,
      ],
    },
    {
      icon: Heart,
      title: t.miscLandChronicCardiacTitle,
      points: [
        t.miscLandChronicCardiac1,
        t.miscLandChronicCardiac2,
        t.miscLandChronicCardiac3,
        t.miscLandChronicHospitalsNearby,
      ],
    },
    {
      icon: Shield,
      title: t.miscLandChronicAutoimmuneTitle,
      points: [
        t.miscLandChronicAutoimmune1,
        t.miscLandChronicAutoimmune2,
        t.miscLandChronicAutoimmune3,
        t.miscLandChronicAutoimmune4,
      ],
    },
    {
      icon: Activity,
      title: t.miscLandChronicKidneyTitle,
      points: [
        t.miscLandChronicKidney1,
        t.miscLandChronicKidney2,
        t.miscLandChronicKidney3,
        t.miscLandChronicHospitalsNearby,
      ],
    },
  ];

  // A card links to its guide only once the guide is published. Until
  // 2026-10-20 all eight linked, and seven answered "Article not found" — to
  // visitors, and to Google, which met them as the landing page's links into
  // the blog. The unwritten ones are in scripts/blog-queue.json; add the href
  // back when the post goes live.
  // Google had crawled only this page by 26 October 2026 and left every post
  // "discovered, not indexed": a link from here is how it reaches them. The
  // posts are English, so the titles are too.
  const moreGuides = [
    {
      href: "/emergency-medical-card",
      title: "Make a free emergency medical card in five languages",
    },
    {
      href: "/travel-medical-records-app",
      title: "An app for your vaccination records and medical history",
    },
    {
      href: "/blog/emergency-medical-id-card-travel",
      title: "What belongs on an emergency medical card",
    },
    {
      href: "/blog/medication-abroad-what-documents-you-need",
      title: "Taking medication abroad: the documents you need",
    },
    {
      href: "/blog/travel-insurance-pre-existing-conditions",
      title: "Travel insurance with pre-existing conditions",
    },
    {
      href: "/blog/insulin-on-plane-tsa-rules",
      title: "Insulin on a plane: TSA rules",
    },
    {
      href: "/blog/flying-after-heart-attack",
      title: "Flying after a heart attack",
    },
    {
      href: "/blog/long-haul-flight-health-chronic-illness",
      title: "Long-haul flights with a chronic illness",
    },
    {
      href: "/blog/health-risks-traveling-thailand",
      title: "Health risks when traveling to Thailand",
    },
    {
      href: "/blog/health-documents-to-enter-mexico",
      title: "Health documents to enter Mexico",
    },
    {
      href: "/blog/health-documents-to-enter-japan",
      title: "Health documents to enter Japan",
    },
  ];

  const conditionGuides: {
    condition: string;
    icon: string;
    features: string[];
    href?: string;
  }[] = [
    {
      condition: t.miscLandChronicDiabetesTitle,
      icon: "💉",
      features: [
        t.miscLandHubDiabetes1,
        t.miscLandHubDiabetes2,
        t.miscLandHubDiabetes3,
      ],
      href: "/blog/insulin-storage-travel",
    },
    {
      condition: t.miscLandHubHeartTitle,
      icon: "❤️",
      features: [
        t.miscLandHubHeart1,
        t.miscLandHubHeart2,
        t.miscLandHubConditionOnQr,
      ],
      href: "/blog/traveling-with-heart-disease",
    },
    {
      condition: t.miscLandHubCopdTitle,
      icon: "🫁",
      features: [t.miscLandHubCopd1, t.miscLandHubCopd2, t.miscLandHubCopd3],
      href: "/blog/traveling-with-copd",
    },
    {
      condition: t.miscLandHubCrohnsTitle,
      icon: "🔬",
      features: [
        t.miscLandHubCrohns1,
        t.miscLandHubCrohns2,
        t.miscLandHubClinics,
      ],
    },
    {
      condition: t.miscLandHubEpilepsyTitle,
      icon: "🧠",
      features: [
        t.miscLandHubEpilepsy1,
        t.miscLandHubEpilepsy2,
        t.miscLandHubHospitals,
      ],
    },
    {
      condition: t.miscLandChronicKidneyTitle,
      icon: "⚕️",
      features: [
        t.miscLandHubKidney1,
        t.miscLandHubKidney2,
        t.miscLandHubHospitals,
      ],
    },
    {
      condition: t.miscLandHubRaTitle,
      icon: "🦴",
      features: [t.miscLandHubRa1, t.miscLandHubRa2, t.miscLandHubClinics],
    },
    {
      condition: t.miscLandHubMsTitle,
      icon: "🧬",
      features: [
        t.miscLandHubMs1,
        t.miscLandHubConditionOnQr,
        t.miscLandHubHospitals,
      ],
    },
  ];

  const useCases = [
    {
      icon: Heart,
      title: t.miscLandCaseGroupTitle,
      subtitle: t.miscLandCaseGroupSubtitle,
      challenge: t.miscLandCaseGroupChallenge,
      solution: t.miscLandCaseGroupSolution,
      results: [
        t.miscLandCaseAllReturned,
        t.miscLandCaseGroupResult2,
        t.miscLandCaseGroupResult3,
      ],
    },
    {
      icon: Plane,
      title: t.miscLandCaseCruiseTitle,
      subtitle: t.miscLandCaseCruiseSubtitle,
      challenge: t.miscLandCaseCruiseChallenge,
      solution: t.miscLandCaseCruiseSolution,
      results: [
        t.miscLandCaseCruiseResult1,
        t.miscLandCaseCruiseResult2,
        t.miscLandCaseCruiseResult3,
      ],
    },
    {
      icon: Users,
      title: t.miscLandCaseFamilyTitle,
      subtitle: t.miscLandCaseFamilySubtitle,
      challenge: t.miscLandCaseFamilyChallenge,
      solution: t.miscLandCaseFamilySolution,
      results: [
        t.miscLandCaseFamilyResult1,
        t.miscLandCaseFamilyResult2,
        t.miscLandCaseFamilyResult3,
      ],
    },
    {
      icon: Globe,
      title: t.miscLandCaseSoloTitle,
      subtitle: t.miscLandCaseSoloSubtitle,
      challenge: t.miscLandCaseSoloChallenge,
      solution: t.miscLandCaseSoloSolution,
      results: [
        t.miscLandCaseSoloResult1,
        t.miscLandCaseSoloResult2,
        t.miscLandCaseSoloResult3,
      ],
    },
    {
      icon: Heart,
      title: t.miscLandCaseSeniorsTitle,
      subtitle: t.miscLandCaseSeniorsSubtitle,
      challenge: t.miscLandCaseSeniorsChallenge,
      solution: t.miscLandCaseSeniorsSolution,
      results: [
        t.miscLandCaseAllReturned,
        t.miscLandCaseSeniorsResult2,
        t.miscLandCaseSeniorsResult3,
      ],
    },
  ];

  // `name` is the plan code: it builds the register link and is shown as the
  // product name, so it is not translated.
  const pricing = [
    {
      name: "FREE",
      price: 0,
      period: t.miscLandPeriodForever,
      features: [
        t.miscLandPlanEmergencyData,
        t.miscLandPlanQrAccess,
        t.miscLandPlanBasicTravel,
        t.miscLandPlanFiveReminders,
        t.miscLandPlanGdprStorage,
      ],
      cta: t.landingStartFree,
      highlight: false,
    },
    {
      name: "PLUS",
      price: 9,
      period: t.miscLandPeriodMonth,
      features: [
        t.miscLandPlanEverythingFree,
        t.miscLandPlanUnlimitedReminders,
        t.miscLandPlanTravelReqs,
        t.miscLandPlanVaccTracking,
        t.miscLandPlanStorage1,
        t.miscLandPlanPriority,
        t.miscLandPlanOffline,
      ],
      cta: t.miscLandChoosePlus,
      highlight: true,
      badge: t.miscLandBadgeMostPopular,
    },
    {
      name: "PREMIUM",
      price: 15,
      period: t.miscLandPeriodMonth,
      features: [
        t.miscLandPlanEverythingPlus,
        t.miscLandPlanCompanions,
        t.miscLandPlanNearby,
        t.miscLandPlanDoctorSummary,
        t.miscLandPlanAdviceWatch,
        t.miscLandPlanStorage10,
      ],
      cta: t.miscLandChoosePremium,
      highlight: false,
    },
  ];

  const singleTripPricing = [
    {
      name: "PLUS SINGLE",
      price: 25,
      period: t.miscLandPeriodOneTrip,
      durationDays: 90,
      features: [
        t.miscLandPlanAllPlus,
        t.miscLandPlanTravelReqs,
        t.miscLandPlanVaccTracking,
        t.miscLandPlanStorage1,
        t.miscLandPlanPriority,
        t.miscLandPlanOffline,
      ],
      cta: t.miscLandBuySingleTrip,
      highlight: true,
      badge: t.miscLandBadgeBestOneTrip,
    },
    {
      name: "PREMIUM SINGLE",
      price: 39,
      period: t.miscLandPeriodOneTrip,
      durationDays: 90,
      features: [
        t.miscLandPlanAllPremium,
        t.miscLandPlanCompanions,
        t.miscLandPlanNearby,
        t.miscLandPlanDoctorSummary,
        t.miscLandPlanAdviceWatch,
        t.miscLandPlanStorage10,
      ],
      cta: t.miscLandBuySingleTrip,
      highlight: false,
    },
  ];

  // Asked of assistants in the words the weekly sweep uses (MAR-009 §7), shown
  // on the page and carried in its FAQPage markup from the same strings.
  const faqPrices = (text: string) =>
    text
      .replace("{plus}", formatPrice(9))
      .replace("{premium}", formatPrice(15))
      .replace("{plusTrip}", formatPrice(25))
      .replace("{premiumTrip}", formatPrice(39));
  const faqs = [
    { question: t.miscLandFaqQ1, answer: t.miscLandFaqA1 },
    { question: t.miscLandFaqQ2, answer: t.miscLandFaqA2 },
    { question: t.miscLandFaqQ3, answer: t.miscLandFaqA3 },
    { question: t.miscLandFaqQ4, answer: faqPrices(t.miscLandFaqA4) },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={t.miscLandSeoTitle}
        description={t.miscLandSeoDescription}
        keywords="travel health passport, travel medical records app, travel health companion, travel health app, emergency medical profile, digital travel health record, travel risk preparation, medical information for travel, travel health checklist"
        url="https://travelhealthpass.app"
        canonical="https://travelhealthpass.app"
      />
      <JsonLd
        data={{
          "@graph": [
            ORGANIZATION,
            {
              "@type": "WebSite",
              "@id": `${SITE_URL}/#website`,
              url: SITE_URL,
              name: "HealthPass",
              publisher: { "@id": ORGANIZATION["@id"] },
            },
          ],
        }}
      />
      <JsonLd
        data={{
          "@type": "FAQPage",
          mainEntity: faqs.map((faq) => ({
            "@type": "Question",
            name: faq.question,
            acceptedAnswer: { "@type": "Answer", text: faq.answer },
          })),
        }}
      />

      <PublicNav />

      {/* Hero section - content depends on the target audience */}
      <main id="main-content">
        <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-primary-50 via-white to-primary-50">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-8">
                <div className="inline-block">
                  <span className="bg-primary-100 text-primary-800 text-sm font-medium px-4 py-2 rounded-full inline-flex items-center gap-2">
                    <Globe className="h-4 w-4 text-primary-700" />
                    {t.miscLandHeroLanguagesBadge}
                  </span>
                </div>
                <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                  {currentHero.headline}
                </h1>
                <p className="text-xl text-gray-600 leading-relaxed">
                  {currentHero.subheadline}
                </p>

                {/* Segmented CTAs */}
                <div className="flex flex-col gap-3">
                  <Link
                    to={currentHero.ctaLink}
                    className="bg-primary-600 text-white px-8 py-4 rounded-lg hover:bg-primary-700 transition font-semibold text-lg flex items-center justify-center group"
                  >
                    {currentHero.cta}
                    <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition" />
                  </Link>

                  {/* Alternative Plans */}
                  <div className="flex flex-wrap gap-2">
                    {audiences.map((label) => (
                      <span
                        key={label}
                        className="text-sm text-gray-600 inline-flex items-center gap-1 rounded-full bg-gray-100 px-3 py-1"
                      >
                        {label}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="flex items-center space-x-6 pt-4">
                  <div className="flex items-center space-x-2">
                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                    <span className="text-sm text-gray-600">
                      {t.landingNoCreditCard}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                    <span className="text-sm text-gray-600">
                      {t.miscLandFreeLifetimePlan}
                    </span>
                  </div>
                </div>
              </div>
              <div className="relative">
                {/* Smartphone Mockup */}
                <div
                  className="relative mx-auto"
                  style={{ width: "320px", height: "640px" }}
                >
                  {/* Phone Frame */}
                  <div className="absolute inset-0 bg-gray-900 rounded-[50px] shadow-2xl border-8 border-gray-800">
                    {/* Notch */}
                    <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-32 h-7 bg-gray-900 rounded-b-3xl z-10"></div>

                    {/* Screen */}
                    <div className="absolute inset-2 bg-white rounded-[42px] overflow-hidden">
                      {/* Status Bar */}
                      <div className="bg-gradient-to-r from-primary-500 to-primary-700 px-6 py-3 flex justify-between items-center text-white text-xs">
                        <span>9:41</span>
                        <div className="flex items-center space-x-1">
                          <Activity className="h-3 w-3" />
                          <span>100%</span>
                        </div>
                      </div>

                      {/* App Header */}
                      <div className="bg-white px-6 py-4 border-b border-gray-200">
                        <div className="flex items-center space-x-2">
                          <img
                            src="/images/healthpass_logo.png"
                            alt="HealthPass"
                            className="h-6 w-auto"
                          />
                        </div>
                      </div>

                      {/* Content */}
                      <div className="p-6 space-y-4 bg-gray-50 h-full">
                        {/* Emergency Card */}
                        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center space-x-2">
                              <div className="h-10 w-10 bg-red-100 rounded-full flex items-center justify-center">
                                <Heart className="h-5 w-5 text-red-600" />
                              </div>
                              <div>
                                <p className="text-sm font-semibold text-gray-900">
                                  {t.miscLandMockEmergencyData}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {t.miscLandMockAlwaysAccessible}
                                </p>
                              </div>
                            </div>
                            <div className="h-16 w-16 bg-gray-100 rounded-lg flex items-center justify-center">
                              <div className="text-[8px] text-center">
                                <div className="font-mono text-lg">QR</div>
                                <div className="text-gray-600">
                                  {t.miscLandMockScan}
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="space-y-2">
                            <div className="flex items-start space-x-2">
                              <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                              <div>
                                <p className="text-xs font-medium text-gray-900">
                                  {t.miscLandMockBloodType}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {t.miscLandMockVerified}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-start space-x-2">
                              <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                              <div>
                                <p className="text-xs font-medium text-gray-900">
                                  {t.miscLandMockAllergies}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {t.miscLandMockSevere}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Travel Card */}
                        <div className="bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl p-4 text-white shadow-sm">
                          <div className="flex items-center space-x-2 mb-2">
                            <Plane className="h-5 w-5" />
                            <p className="text-sm font-semibold">
                              {t.miscLandMockUpcomingTrip}
                            </p>
                          </div>
                          <p className="text-lg font-bold">
                            {t.miscLandMockTripDestination}
                          </p>
                          <div className="flex items-center space-x-2 mt-2 text-xs opacity-90">
                            <Calendar className="h-3 w-3" />
                            <span>{mockTripDates}</span>
                          </div>
                        </div>

                        {/* Medication Reminder */}
                        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
                          <div className="flex items-center space-x-2 mb-2">
                            <Bell className="h-4 w-4 text-primary-600" />
                            <p className="text-xs font-semibold text-gray-900">
                              {t.miscLandMockMedReminder}
                            </p>
                          </div>
                          <p className="text-xs text-gray-700">Aspirin 100mg</p>
                          <p className="text-xs text-gray-500">
                            {t.miscLandMockDailyAt.replace(
                              "{time}",
                              mockReminderTime,
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Floating elements */}
                <div className="absolute -bottom-4 -left-4 bg-primary-600 text-white px-4 py-2 rounded-full shadow-lg text-sm font-medium inline-flex items-center gap-2">
                  <Lock className="h-4 w-4" />
                  {t.miscLandMockEncrypted}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Install the app - directly below the hero, where a visitor on a
            laptop first looks and can be moved onto their phone. */}
        <QuickInstallSection />

        {/* Trust and readiness badges */}
        <section className="py-12 bg-gray-50 border-y border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-wrap justify-center items-center gap-8">
              <div className="flex items-center gap-3 text-gray-600">
                <LandingIcon icon={Shield} className="h-12 w-12 rounded-xl" />
                <span className="font-medium">{t.miscLandTrustPrivacy}</span>
              </div>
              <div className="flex items-center gap-3 text-gray-600">
                <LandingIcon
                  icon={CheckCircle2}
                  className="h-12 w-12 rounded-xl"
                />
                <span className="font-medium">
                  {t.miscLandTrustSecureAccess}
                </span>
              </div>
              <div className="flex items-center gap-3 text-gray-600">
                <LandingIcon icon={Users} className="h-12 w-12 rounded-xl" />
                <span className="font-medium">
                  {t.miscLandTrustManySituations}
                </span>
              </div>
              <div className="flex items-center gap-3 text-gray-600">
                <LandingIcon icon={Globe} className="h-12 w-12 rounded-xl" />
                <span className="font-medium">
                  {t.miscLandTrustInternational}
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* HEALTHPASS Card showcase */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-50 via-primary-50 to-primary-50">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full bg-primary-100 px-4 py-2 text-sm font-medium text-primary-800 mb-4">
                  <Shield className="h-4 w-4" />
                  {t.miscLandCardBadge}
                </div>
                <h2 className="text-4xl font-bold text-gray-900 mb-4">
                  {t.miscLandCardTitle}
                </h2>
                <p className="text-xl text-gray-600 mb-6 max-w-2xl">
                  {t.miscLandCardIntro}
                </p>
                <div className="space-y-3 text-gray-700 mb-8">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 shrink-0" />
                    <span>{t.miscLandCardPoint1}</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 shrink-0" />
                    <span>{t.miscLandCardPoint2}</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 shrink-0" />
                    <span>{t.miscLandCardPoint3}</span>
                  </div>
                </div>
                <Link
                  to="/traveler/register"
                  className="inline-flex items-center bg-primary-600 text-white px-8 py-4 rounded-lg hover:bg-primary-700 transition font-semibold text-lg"
                >
                  {t.miscLandCtaCreateFreeAccount}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </div>

              <div className="flex flex-col items-center">
                <div className="w-full flex justify-center">
                  <div className="origin-top scale-[1.02] sm:scale-110">
                    <HealthPassCardFront
                      cardData={landingDemoCardData}
                      qrData={landingDemoQrData}
                    />
                  </div>
                </div>

                <div className="mt-8 grid grid-cols-2 gap-4 w-full max-w-2xl">
                  <Link
                    to="/traveler/health/checkin"
                    className="rounded-2xl border-2 border-primary-200 bg-white p-5 text-center transition hover:-translate-y-0.5 hover:border-primary-300 hover:shadow-md"
                  >
                    <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary-100">
                      <QrCode className="h-6 w-6 text-primary-600" />
                    </div>
                    <div className="text-primary-700 font-bold tracking-wide uppercase text-sm">
                      {t.miscLandCardActionCheckIn}
                    </div>
                  </Link>
                  <Link
                    to="/traveler/health/emergency"
                    className="rounded-2xl border-2 border-red-200 bg-white p-5 text-center transition hover:-translate-y-0.5 hover:border-red-300 hover:shadow-md"
                  >
                    <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-red-100">
                      <Shield className="h-6 w-6 text-red-600" />
                    </div>
                    <div className="text-red-700 font-bold tracking-wide uppercase text-sm">
                      {t.miscLandCardActionEmergency}
                    </div>
                  </Link>
                  <Link
                    to="/traveler/card"
                    className="rounded-2xl border-2 border-teal-200 bg-white p-5 text-center transition hover:-translate-y-0.5 hover:border-teal-300 hover:shadow-md"
                  >
                    <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-teal-100">
                      <Stethoscope className="h-6 w-6 text-teal-600" />
                    </div>
                    <div className="text-teal-700 font-bold tracking-wide uppercase text-sm">
                      {t.miscLandCardActionMedAccess}
                    </div>
                  </Link>
                  <Link
                    to="/traveler/card/wallet"
                    className="rounded-2xl border-2 border-primary-200 bg-white p-5 text-center transition hover:-translate-y-0.5 hover:border-primary-300 hover:shadow-md"
                  >
                    <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary-100">
                      <Wallet className="h-6 w-6 text-primary-600" />
                    </div>
                    <div className="text-primary-700 font-bold tracking-wide uppercase text-sm">
                      {t.miscLandCardActionWallet}
                    </div>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Chronic Illness Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-primary-50 to-pink-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                {t.miscLandChronicTitle}
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                {t.miscLandChronicIntro}
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 mb-12">
              {chronicCards.map((card) => (
                <div
                  key={card.title}
                  className="bg-white p-6 rounded-xl shadow-sm border border-gray-200"
                >
                  <h3 className="text-lg font-bold text-gray-900 mb-3">
                    <span className="inline-flex items-center gap-2">
                      <card.icon className="h-5 w-5 text-slate-700" />
                      {card.title}
                    </span>
                  </h3>
                  <ul className="text-gray-700 space-y-2 text-sm">
                    {card.points.map((point) => (
                      <li key={point}>✓ {point}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-lg border-2 border-primary-200">
              <div className="flex items-start space-x-4">
                <LandingIcon icon={Heart} className="shrink-0" />
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    {t.miscLandConfidenceTitle}
                  </h3>
                  <p className="text-gray-700 mb-4">
                    {t.miscLandConfidenceText}
                  </p>
                  <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded mb-4">
                    <div className="space-y-2 text-green-800 font-medium">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="h-5 w-5" />
                        <span>{t.miscLandConfidencePoint1}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="h-5 w-5" />
                        <span>{t.miscLandConfidencePoint2}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="h-5 w-5" />
                        <span>{t.miscLandConfidencePoint3}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="h-5 w-5" />
                        <span>{t.miscLandConfidencePoint4}</span>
                      </div>
                    </div>
                  </div>
                  <blockquote className="border-l-4 border-primary-500 pl-4 italic text-gray-700">
                    {t.miscLandConfidenceQuote}
                  </blockquote>
                </div>
              </div>
            </div>

            <div className="text-center mt-12">
              <p className="text-gray-600 mb-6">
                <strong>{t.miscLandStatsMillions}</strong>{" "}
                {t.miscLandStatsLiveWith} <strong>{percentAvoiding}</strong>{" "}
                {t.miscLandStatsAvoid}{" "}
                <strong>{t.miscLandStatsReconsider}</strong>
              </p>
              <Link
                to="/traveler/register"
                className="inline-flex items-center bg-primary-600 text-white px-8 py-4 rounded-lg hover:bg-primary-700 transition font-semibold text-lg"
              >
                {t.miscLandStartJourney}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </div>
          </div>
        </section>

        {/* Why HEALTHPASS matters */}
        <section
          id="why-healthpass"
          className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-primary-50 to-primary-50"
        >
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold mb-4 text-gray-900">
                {t.miscLandWhyTitle}
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                {t.miscLandWhyIntro}
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-8 mb-10">
              <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-200">
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                  {t.miscLandWhyPrepTitle}
                </h3>
                <p className="text-gray-700 mb-4 leading-relaxed">
                  {t.miscLandWhyPrepText1}
                </p>
                <p className="text-gray-700 leading-relaxed">
                  {t.miscLandWhyPrepText2}
                </p>
              </div>

              <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-200">
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                  {t.miscLandWhySeamlessTitle}
                </h3>
                <p className="text-gray-700 mb-4 leading-relaxed">
                  {t.miscLandWhySeamlessText1}
                </p>
                <p className="text-gray-700 leading-relaxed">
                  {t.miscLandWhySeamlessText2}
                </p>
              </div>
            </div>

            <div className="bg-primary-600 text-white rounded-2xl p-6 text-center font-medium text-lg mb-12">
              {t.miscLandWhyBanner}
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {audiences.map((group) => (
                <div
                  key={group}
                  className="bg-white p-5 rounded-xl shadow-sm border border-gray-200 text-center font-medium text-gray-800"
                >
                  {group}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Travel support for organized, safer trips */}
        <section
          id="travel-support"
          className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-primary-900 to-primary-800 text-white"
        >
          <div className="max-w-7xl mx-auto">
            <h2 className="text-4xl font-bold mb-4 text-center inline-flex items-center justify-center gap-3 w-full">
              <Plane className="h-8 w-8 text-slate-200" />
              {t.miscLandSupportTitle}
            </h2>
            <p className="text-xl opacity-90 mb-12 text-center max-w-3xl mx-auto">
              {t.miscLandSupportIntro}
            </p>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
              <div className="text-center">
                <div className="mb-2 flex justify-center">
                  <LandingIcon
                    icon={Activity}
                    className="border-white/20 bg-white/10 text-white shadow-none"
                  />
                </div>
                <h3 className="font-bold mb-1">{t.miscLandSupportRiskTitle}</h3>
                <p className="text-sm opacity-90">
                  {t.miscLandSupportRiskText}
                </p>
              </div>
              <div className="text-center">
                <div className="mb-2 flex justify-center">
                  <LandingIcon
                    icon={Building2}
                    className="border-white/20 bg-white/10 text-white shadow-none"
                  />
                </div>
                <h3 className="font-bold mb-1">
                  {t.miscLandSupportFasterTitle}
                </h3>
                <p className="text-sm opacity-90">
                  {t.miscLandSupportFasterText}
                </p>
              </div>
              <div className="text-center">
                <div className="mb-2 flex justify-center">
                  <LandingIcon
                    icon={BarChart3}
                    className="border-white/20 bg-white/10 text-white shadow-none"
                  />
                </div>
                <h3 className="font-bold mb-1">
                  {t.miscLandSupportRepetitionTitle}
                </h3>
                <p className="text-sm opacity-90">
                  {t.miscLandSupportRepetitionText}
                </p>
              </div>
              <div className="text-center">
                <div className="mb-2 flex justify-center">
                  <LandingIcon
                    icon={Users}
                    className="border-white/20 bg-white/10 text-white shadow-none"
                  />
                </div>
                <h3 className="font-bold mb-1">
                  {t.miscLandSupportConfidenceTitle}
                </h3>
                <p className="text-sm opacity-90">
                  {t.miscLandSupportConfidenceText}
                </p>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur p-8 rounded-2xl mb-12 text-center">
              <h3 className="text-2xl font-bold mb-4">{t.miscLandDemoTitle}</h3>
              <p className="text-lg opacity-90 mb-6 max-w-3xl mx-auto">
                {t.miscLandDemoText}
              </p>
              <Link
                to="/book-a-demo"
                className="inline-flex items-center bg-white text-primary-900 px-10 py-3 rounded-lg hover:bg-gray-100 transition font-bold text-lg"
              >
                {t.miscLandDemoCta}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </div>
          </div>
        </section>

        {/* Travelers with health issues */}
        <section
          id="chronic-illness"
          className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-primary-50 to-pink-50"
        >
          <div className="max-w-7xl mx-auto">
            <h2 className="text-4xl font-bold mb-4 text-center inline-flex items-center justify-center gap-3 w-full">
              <Globe className="h-8 w-8 text-slate-700" />
              {t.miscLandIssuesTitle}
            </h2>
            <p className="text-xl text-gray-600 mb-12 text-center max-w-3xl mx-auto">
              {t.miscLandIssuesIntro}
            </p>

            <div className="bg-white p-8 rounded-2xl shadow-lg mb-12">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <div className="flex items-start space-x-4 mb-4">
                    <LandingIcon icon={Stethoscope} className="shrink-0" />
                    <div>
                      <h3 className="text-2xl font-bold mb-2">
                        {t.miscLandIssuesFearTitle}
                      </h3>
                      <p className="text-gray-600">
                        {t.miscLandIssuesFearSubtitle}
                      </p>
                    </div>
                  </div>

                  <p className="text-gray-700 mb-4">{t.miscLandIssuesText1}</p>

                  <p className="text-gray-700 mb-4">{t.miscLandIssuesText2}</p>
                </div>

                <div className="bg-green-50 p-6 rounded-xl">
                  <p className="font-semibold mb-4 text-lg">
                    {t.miscLandIssuesHelpTitle}
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <span className="text-green-600 mr-2 text-xl">✓</span>
                      <span>{t.miscLandIssuesHelp1}</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-600 mr-2 text-xl">✓</span>
                      <span>{t.miscLandIssuesHelp2}</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-600 mr-2 text-xl">✓</span>
                      <span>{t.miscLandIssuesHelp3}</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-600 mr-2 text-xl">✓</span>
                      <span>{t.miscLandIssuesHelp4}</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Chronic Care CTA */}
            <div className="text-center">
              <Link
                to="/traveler/register?plan=chronic"
                className="inline-flex items-center bg-primary-600 text-white px-12 py-4 rounded-lg hover:bg-primary-700 transition font-semibold text-lg"
              >
                {t.miscLandIssuesCta}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
              <p className="text-sm text-gray-500 mt-4">
                {t.miscLandIssuesCtaNote.replace("{price}", formatPrice(9))}
              </p>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section id="features" className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                {t.landingFeaturesTitle}
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                {t.landingFeaturesSubtitle}
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="bg-white p-8 rounded-xl border border-gray-200 hover:shadow-lg transition group"
                >
                  <LandingIcon
                    icon={feature.icon}
                    className="mb-4 group-hover:border-slate-300 group-hover:bg-slate-100"
                  />
                  <h3 className="text-lg sm:text-xl font-semibold leading-snug tracking-tight text-gray-900 mb-2">
                    {feature.title}
                  </h3>
                  <p className="leading-relaxed text-slate-600">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Conditions Hub – SEO anchor section */}
        <section
          id="conditions"
          className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-primary-50 to-white"
          aria-labelledby="conditions-heading"
        >
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2
                id="conditions-heading"
                className="text-4xl font-bold text-gray-900 mb-4"
              >
                {t.miscLandHubTitle}
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                {t.miscLandHubIntro}
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
              {conditionGuides.map((item) => {
                const Card = item.href ? "a" : "div";
                return (
                  <Card
                    key={item.condition}
                    {...(item.href ? { href: item.href } : {})}
                    className={
                      item.href
                        ? "bg-white rounded-xl p-5 border border-gray-200 hover:border-primary-400 hover:shadow-md transition group block"
                        : "bg-white rounded-xl p-5 border border-gray-200 block"
                    }
                  >
                    <div className="text-3xl mb-3">{item.icon}</div>
                    <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-primary-600 transition">
                      {item.condition}
                    </h3>
                    <ul className="space-y-1">
                      {item.features.map((f) => (
                        <li
                          key={f}
                          className="text-sm text-gray-500 flex items-center gap-1.5"
                        >
                          <CheckCircle2 className="h-3.5 w-3.5 text-green-500 flex-shrink-0" />
                          {f}
                        </li>
                      ))}
                    </ul>
                    {item.href && (
                      <span className="mt-3 inline-flex items-center text-xs text-primary-600 font-medium">
                        {t.miscLandHubReadGuide}{" "}
                        <ArrowRight className="h-3 w-3 ml-1" />
                      </span>
                    )}
                  </Card>
                );
              })}
            </div>

            <nav aria-labelledby="more-guides-heading" className="mb-12">
              <h3
                id="more-guides-heading"
                className="text-lg font-semibold text-gray-900 mb-4 text-center"
              >
                {t.miscLandHubMoreGuides}
              </h3>
              <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-2 max-w-4xl mx-auto">
                {moreGuides.map((guide) => (
                  <li key={guide.href}>
                    <a
                      href={guide.href}
                      className="text-primary-600 hover:underline"
                    >
                      {guide.title}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>

            <div className="text-center">
              <Link
                to="/traveler/register?plan=chronic"
                className="inline-flex items-center bg-primary-600 text-white px-8 py-4 rounded-lg hover:bg-primary-700 transition font-semibold text-lg"
              >
                {t.miscLandHubCta}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
              <p className="mt-3 text-sm text-gray-500">{t.miscLandHubNote}</p>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section
          id="how-it-works"
          className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50"
        >
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                {t.landingHowItWorksTitle}
              </h2>
              <p className="text-xl text-gray-600">
                {t.landingHowItWorksSubtitle}
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              {steps.map((step, index) => (
                <div key={index} className="relative">
                  <div className="bg-white p-8 rounded-xl border border-gray-200 h-full">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary-600 text-white text-2xl font-bold mb-6">
                      {step.number}
                    </div>
                    <h3 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-3">
                      {step.title}
                    </h3>
                    <p className="text-gray-600 text-lg">{step.description}</p>
                  </div>
                  {index < steps.length - 1 && (
                    <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                      <ArrowRight className="h-8 w-8 text-gray-300" />
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="text-center mt-12">
              <Link
                to="/traveler/register"
                className="inline-flex items-center bg-primary-600 text-white px-8 py-4 rounded-lg hover:bg-primary-700 transition font-semibold text-lg"
              >
                {t.landingStartFree}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </div>
          </div>
        </section>

        {/* Use Cases */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-primary-50 to-primary-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                {t.miscLandCasesTitle}
              </h2>
              <p className="text-xl text-gray-600">{t.miscLandCasesIntro}</p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {useCases.map((useCase) => (
                <div
                  key={useCase.title}
                  className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-xl transition"
                >
                  <div className="flex items-start space-x-4 mb-6">
                    <LandingIcon icon={useCase.icon} className="shrink-0" />
                    <div>
                      <h3 className={landingStoryTitleClassName}>
                        {useCase.title}
                      </h3>
                      <p className="text-gray-600">{useCase.subtitle}</p>
                    </div>
                  </div>

                  <div className="space-y-4 mb-6">
                    <p className="text-gray-700">
                      <strong>{t.miscLandChallenge}</strong> {useCase.challenge}
                    </p>
                    <p className="text-gray-700">
                      <strong>{t.miscLandSolution}</strong> {useCase.solution}
                    </p>
                    <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded">
                      <div className="space-y-2 text-green-800 font-medium">
                        {useCase.results.map((result) => (
                          <div key={result} className="flex items-center gap-2">
                            <CheckCircle2 className="h-5 w-5" />
                            <span>{result}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center mt-12">
              <Link
                to="/blog"
                className="inline-flex items-center text-primary-600 hover:text-primary-700 transition font-semibold text-lg"
              >
                {t.miscLandCasesMore}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </div>
          </div>
        </section>

        {/* Pricing */}
        <section id="pricing" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                {t.landingPricingTitle}
              </h2>
              <p className="text-xl text-gray-600">
                {t.landingPricingSubtitle}
              </p>
            </div>

            {/* Monthly Subscriptions */}
            <div className="text-center mb-8">
              <h3 className="text-2xl font-semibold text-gray-800">
                {t.miscLandPricingMonthlyTitle}
              </h3>
              <p className="text-gray-500 mt-1">
                {t.miscLandPricingMonthlyNote}
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto mb-16">
              {pricing.map((plan, index) => (
                <div
                  key={index}
                  className={`bg-white rounded-2xl p-8 ${
                    plan.highlight
                      ? "border-2 border-primary-600 shadow-xl relative"
                      : "border border-gray-200"
                  }`}
                >
                  {plan.badge && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <span className="bg-primary-600 text-white px-4 py-1 rounded-full text-sm font-medium">
                        {plan.badge}
                      </span>
                    </div>
                  )}
                  <div className="text-center mb-8">
                    <h3 className={landingStoryTitleClassName}>{plan.name}</h3>
                    <div className="flex items-baseline justify-center">
                      <span className="text-5xl font-bold text-gray-900">
                        {formatPrice(plan.price)}
                      </span>
                      <span className="text-gray-500 ml-2">/{plan.period}</span>
                    </div>
                  </div>
                  <ul className="space-y-4 mb-8">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-start">
                        <CheckCircle2 className="h-5 w-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to={`/traveler/register${planQuery(plan.name)}`}
                    className={`block w-full py-3 rounded-lg font-semibold text-center transition ${
                      plan.highlight
                        ? "bg-primary-600 text-white hover:bg-primary-700"
                        : "bg-gray-100 text-gray-900 hover:bg-gray-200"
                    }`}
                  >
                    {plan.cta}
                  </Link>
                </div>
              ))}
            </div>

            {/* Single Trip Plans */}
            <div className="text-center mb-8">
              <h3 className="text-2xl font-semibold text-gray-800">
                {t.miscLandSingleTitle}
              </h3>
              <p className="text-gray-500 mt-1">{t.miscLandSingleNote}</p>
            </div>
            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {singleTripPricing.map((plan, index) => (
                <div
                  key={index}
                  className={`bg-white rounded-2xl p-8 ${
                    plan.highlight
                      ? "border-2 border-emerald-600 shadow-xl relative"
                      : "border border-gray-200"
                  }`}
                >
                  {plan.badge && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <span className="bg-emerald-700 text-white px-4 py-1 rounded-full text-sm font-medium">
                        {plan.badge}
                      </span>
                    </div>
                  )}
                  <div className="text-center mb-8">
                    <h3 className={landingStoryTitleClassName}>{plan.name}</h3>
                    <div className="flex items-baseline justify-center">
                      <span className="text-5xl font-bold text-gray-900">
                        {formatPrice(plan.price)}
                      </span>
                      <span className="text-gray-500 ml-2">/{plan.period}</span>
                    </div>
                    <span className="inline-block mt-2 bg-emerald-50 text-emerald-700 text-sm font-medium px-3 py-1 rounded-full">
                      {t.miscLandSingleAccess.replace(
                        "{days}",
                        fmt.number(plan.durationDays),
                      )}
                    </span>
                  </div>
                  <ul className="space-y-4 mb-8">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-start">
                        <CheckCircle2 className="h-5 w-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to={`/traveler/register${planQuery(plan.name)}`}
                    className={`block w-full py-3 rounded-lg font-semibold text-center transition ${
                      plan.highlight
                        ? "bg-emerald-700 text-white hover:bg-emerald-800"
                        : "bg-gray-100 text-gray-900 hover:bg-gray-200"
                    }`}
                  >
                    {plan.cta}
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="faq" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-10 text-center">
              {t.miscLandFaqTitle}
            </h2>
            <dl className="space-y-8">
              {faqs.map((faq) => (
                <div key={faq.question}>
                  <dt className="text-xl font-semibold text-gray-900 mb-2">
                    {faq.question}
                  </dt>
                  <dd className="text-gray-700 leading-relaxed">
                    {faq.answer}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </section>

        {/* CTA Section */}
        <AppInstallSection />

        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-primary-500 to-primary-700">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h2 className="text-4xl font-bold mb-6">{t.landingCtaTitle}</h2>
            <p className="text-xl mb-8 opacity-90">{t.landingCtaSubtitle}</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/traveler/register"
                className="bg-white text-primary-600 px-8 py-4 rounded-lg hover:bg-gray-100 transition font-semibold text-lg"
              >
                {t.landingCtaButton}
              </Link>
              <Link
                to="/traveler/login"
                className="border-2 border-white text-white px-8 py-4 rounded-lg hover:bg-white/10 transition font-semibold text-lg"
              >
                {t.miscLandSignIn}
              </Link>
            </div>
            <p className="mt-6 text-sm opacity-75">{t.miscLandCtaNote}</p>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center mb-4">
                <img
                  src="/images/healthpass_logo.png"
                  alt={t.miscLandLogoAlt}
                  className="h-8 w-auto brightness-0 invert"
                />
              </div>
              <p className="text-gray-400 text-sm">{t.miscLandFooterTagline}</p>
            </div>
            <div>
              <h3 className="font-semibold mb-4">{t.miscLandFooterProduct}</h3>
              <ul className="space-y-2 text-sm text-gray-400">
                <li>
                  <a href="#features" className="hover:text-white transition">
                    {t.miscLandFooterFeatures}
                  </a>
                </li>
                <li>
                  <a href="#pricing" className="hover:text-white transition">
                    {t.miscLandFooterPricing}
                  </a>
                </li>
                <li>
                  <a
                    href="#get-the-app"
                    className="hover:text-white transition"
                  >
                    {t.appInstallTitle}
                  </a>
                </li>
                <li>
                  <a href="/security" className="hover:text-white transition">
                    {t.miscLandFooterSecurity}
                  </a>
                </li>
                <li>
                  <a href="/faq" className="hover:text-white transition">
                    {t.miscLandFooterFaq}
                  </a>
                </li>
                <li>
                  <a
                    href="/travel-medical-records-app"
                    className="hover:text-white transition"
                  >
                    {t.miscLandFooterRecordsApp}
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-4">{t.miscLandFooterCompany}</h3>
              <ul className="space-y-2 text-sm text-gray-400">
                <li>
                  <a href="/blog" className="hover:text-white transition">
                    {t.miscLandFooterBlog}
                  </a>
                </li>
                <li>
                  <a href="/contact" className="hover:text-white transition">
                    {t.miscLandFooterContact}
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-4">{t.miscLandFooterLegal}</h3>
              <ul className="space-y-2 text-sm text-gray-400">
                <li>
                  <a href="/privacy" className="hover:text-white transition">
                    {t.miscLandFooterPrivacy}
                  </a>
                </li>
                <li>
                  <a href="/terms" className="hover:text-white transition">
                    {t.miscLandFooterTerms}
                  </a>
                </li>
                <li>
                  <a href="/imprint" className="hover:text-white transition">
                    {t.miscLandFooterImprint}
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-sm text-gray-400">
            <p className="inline-flex items-center gap-2">
              <span>{t.miscLandFooterCopyright.replace("{year}", "2026")}</span>
            </p>
          </div>
        </div>
      </footer>

      {/* Through Helmet, not an inline <script>: rendered on the server, an
          inline script's JSON is HTML-escaped into something no parser reads.
          The landing page used to carry an FAQPage whose questions it did not
          show; the one above is built from the questions it does show. The
          offers are the plans the pricing section displays, in its euros. */}
      <JsonLd
        data={{
          "@type": "SoftwareApplication",
          name: "HealthPass",
          url: SITE_URL,
          applicationCategory: "HealthApplication",
          operatingSystem: "Web, Android, iOS (PWA)",
          description:
            "Travel health companion for international travel preparation, emergency access, and digital health information sharing.",
          featureList: [
            "Emergency QR Code for important travel health information",
            "FHIR-based digital health records",
            "Privacy-focused data handling",
            "Multilingual travel support",
            "Medication tracking and reminders",
            "Travel health preparation tools",
          ],
          offers: [...pricing, ...singleTripPricing].map((plan) => ({
            "@type": "Offer",
            name: plan.name,
            price: plan.price.toFixed(2),
            priceCurrency,
          })),
          publisher: { "@id": ORGANIZATION["@id"] },
        }}
      />

      {/* Open Graph Meta Tags */}
      <meta property="og:type" content="website" />
      <meta
        property="og:title"
        content="HealthPass – Travel Health App for Chronic Illness & Emergency Medical ID"
      />
      <meta
        property="og:description"
        content="Travel with confidence. Emergency QR code, multilingual FHIR health records, and medication tracking for travelers with chronic conditions. Free for life."
      />
      <meta
        property="og:image"
        content="https://travelhealthpass.app/images/healthpass_og.png"
      />
      <meta property="og:url" content="https://travelhealthpass.app" />
      <meta property="og:site_name" content="HealthPass" />
      <meta property="og:locale" content="en_US" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta
        name="twitter:title"
        content="HealthPass – Travel Health App for Chronic Illness & Emergency Medical ID"
      />
      <meta
        name="twitter:description"
        content="Emergency QR code, FHIR health records, medication tracking. Built for travelers with chronic conditions. Free for life."
      />
      <meta
        name="twitter:image"
        content="https://travelhealthpass.app/images/healthpass_og.png"
      />
    </div>
  );
}
